import { useEffect, useState } from "react";
import PortalLayout from "../components/PortalLayout";
import { api, formatDate } from "../lib/api";
import { useI18n } from "../i18n/LanguageContext";

export default function StatementsPage() {
  const { locale, t } = useI18n();
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get("/statements/me")
      .then(({ data }) => setItems(data))
      .catch((e) => setError(e.response?.data?.detail || e.message));
  }, []);

  const kindLabel = (kind) => t(`stmt.kind_${kind}`) || kind;

  return (
    <PortalLayout>
      <section className="max-w-[1280px] mx-auto px-6 sm:px-8 pt-14 pb-8" data-testid="statements-page">
        <div className="br-fade-up">
          <div className="br-overline">{t("stmt.overline")}</div>
          <h1 className="font-serif-display text-[clamp(2.4rem,4.5vw,3.6rem)] mt-3">
            {t("stmt.heading_a")} <em>{t("stmt.heading_em")}</em>{t("stmt.heading_b")}
          </h1>
          <p className="text-[1.05rem] text-[color:var(--br-text)] mt-3 max-w-[640px]">
            {t("stmt.intro")}
          </p>
        </div>

        {error && <div className="mt-6 br-card p-4 text-[color:var(--br-red)]">{String(error)}</div>}

        <div className="mt-10 br-card overflow-x-auto br-fade-up br-delay-1" data-testid="statements-table-wrapper">
          <table className="w-full text-left border-collapse" data-testid="statements-table">
            <thead>
              <tr style={{ borderBottom: "1px solid var(--br-line)" }}>
                <Th>{t("stmt.col_period")}</Th>
                <Th>{t("stmt.col_kind")}</Th>
                <Th>{t("stmt.col_label")}</Th>
                <Th>{t("stmt.col_published")}</Th>
                <Th align="right">{t("stmt.col_actions")}</Th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-10 text-center text-[color:var(--br-text-dim)]">
                    {t("stmt.empty")}
                  </td>
                </tr>
              ) : (
                items.map((s) => (
                  <tr key={s.id} className="border-b" style={{ borderColor: "var(--br-line)" }} data-testid={`statement-row-${s.id}`}>
                    <Td className="font-mono-fin" style={{ color: "var(--br-bronze)" }}>{s.period}</Td>
                    <Td className="font-mono-fin text-[color:var(--br-text-dim)]">{kindLabel(s.kind)}</Td>
                    <Td>
                      <span className="font-serif-display italic text-[1.05rem] text-[color:var(--br-parchment)]">
                        {s.label || t("stmt.label_default")}
                      </span>
                    </Td>
                    <Td className="font-mono-fin text-[color:var(--br-text-dim)]">{formatDate(s.created_at, locale)}</Td>
                    <Td align="right">
                      <a
                        href={s.file_url}
                        target="_blank"
                        rel="noreferrer"
                        className="br-btn-outline"
                        style={{ padding: "8px 14px", display: "inline-block" }}
                        data-testid={`download-statement-${s.id}`}
                      >
                        {t("stmt.download")}
                      </a>
                    </Td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </PortalLayout>
  );
}

function Th({ children, align = "left" }) {
  return (
    <th
      className="px-5 py-4 font-mono-fin text-[0.62rem] tracking-[0.22em] uppercase font-medium"
      style={{ color: "var(--br-text-dim)", textAlign: align }}
    >
      {children}
    </th>
  );
}

function Td({ children, align = "left", className = "", style }) {
  return (
    <td className={`px-5 py-5 text-[0.9rem] ${className}`} style={{ textAlign: align, ...style }}>
      {children}
    </td>
  );
}
